<template>
  <div class="ns-page py-3">
    <header class="ns-toolbar">
      <span class="ns-toolbar__accent" aria-hidden="true"></span>
      <div class="ns-toolbar__title">
        <h1>Network Speed</h1>
        <p>Check the ping, download and upload speed of this connection</p>
      </div>
      <div class="ns-toolbar__actions">
        <button
          type="button"
          class="ns-btn ns-btn--primary"
          :disabled="isRunning"
          @click="runTest"
        >
          <svg
            v-if="!isRunning"
            class="ns-btn__icon"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polygon points="5 3 19 12 5 21 5 3"/>
          </svg>
          <span v-else class="ns-spinner" aria-hidden="true"></span>
          <span>{{ isRunning ? 'Running…' : (hasResult ? 'Run again' : 'Start test') }}</span>
        </button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="ns-alert">{{ errorMessage }}</CAlert>

    <section class="ns-gauge-card">
      <div class="ns-gauge-wrap">
        <svg class="ns-gauge" viewBox="0 0 220 220" aria-hidden="true">
          <defs>
            <linearGradient id="nsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#0e5b46"/>
              <stop offset="100%" stop-color="#16a07a"/>
            </linearGradient>
          </defs>
          <circle
            cx="110"
            cy="110"
            r="92"
            fill="none"
            stroke="#eef1f7"
            stroke-width="14"
          />
          <circle
            cx="110"
            cy="110"
            r="92"
            fill="none"
            stroke="url(#nsGradient)"
            stroke-width="14"
            stroke-linecap="round"
            :stroke-dasharray="gaugeCircumference"
            :stroke-dashoffset="gaugeDashOffset"
            transform="rotate(-90 110 110)"
            class="ns-gauge__arc"
          />
        </svg>
        <div class="ns-gauge__center">
          <div class="ns-gauge__value">{{ liveValueLabel }}</div>
          <div class="ns-gauge__unit">{{ liveUnitLabel }}</div>
          <div class="ns-gauge__phase">{{ phaseLabel }}</div>
        </div>
      </div>

      <ol class="ns-steps" :aria-label="phaseLabel">
        <li
          v-for="step in steps"
          :key="step.key"
          :class="['ns-step', stepStatusClass(step.key)]"
        >
          <span class="ns-step__dot" aria-hidden="true">
            <svg
              v-if="stepStatus(step.key) === 'done'"
              viewBox="0 0 24 24"
              width="11"
              height="11"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span v-else-if="stepStatus(step.key) === 'active'" class="ns-step__pulse"></span>
          </span>
          <span class="ns-step__label">{{ step.label }}</span>
        </li>
      </ol>
    </section>

    <section class="ns-metrics">
      <div class="ns-metric">
        <div class="ns-metric__head">
          <span class="ns-metric__icon ns-metric__icon--ping" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2 12h4l3-9 6 18 3-9h4"/>
            </svg>
          </span>
          <span class="ns-metric__label">Ping</span>
        </div>
        <div class="ns-metric__value">
          <span class="ns-metric__num">{{ formatPing(results.ping) }}</span>
          <span class="ns-metric__unit">ms</span>
        </div>
        <div class="ns-metric__sub">Jitter {{ formatPing(results.jitter) }} ms</div>
      </div>

      <div class="ns-metric">
        <div class="ns-metric__head">
          <span class="ns-metric__icon ns-metric__icon--down" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </span>
          <span class="ns-metric__label">Download</span>
        </div>
        <div class="ns-metric__value">
          <span class="ns-metric__num">{{ formatSpeed(results.download) }}</span>
          <span class="ns-metric__unit">Mbps</span>
        </div>
        <div class="ns-metric__sub">{{ qualityHint(results.download, 'down') }}</div>
      </div>

      <div class="ns-metric">
        <div class="ns-metric__head">
          <span class="ns-metric__icon ns-metric__icon--up" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </span>
          <span class="ns-metric__label">Upload</span>
        </div>
        <div class="ns-metric__value">
          <span class="ns-metric__num">{{ formatSpeed(results.upload) }}</span>
          <span class="ns-metric__unit">Mbps</span>
        </div>
        <div class="ns-metric__sub">{{ qualityHint(results.upload, 'up') }}</div>
      </div>
    </section>

    <section v-if="verdict" :class="['ns-verdict', `ns-verdict--${verdict.tone}`]">
      <span class="ns-verdict__badge" aria-hidden="true">
        <svg
          v-if="verdict.tone === 'excellent' || verdict.tone === 'good'"
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        <svg
          v-else-if="verdict.tone === 'workable'"
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="12" y1="8" x2="12" y2="13"/>
          <line x1="12" y1="16.5" x2="12" y2="17"/>
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="6" y1="6" x2="18" y2="18"/>
          <line x1="18" y1="6" x2="6" y2="18"/>
        </svg>
      </span>
      <div class="ns-verdict__body">
        <div class="ns-verdict__title">{{ verdict.title }}</div>
        <div class="ns-verdict__message">{{ verdict.message }}</div>
      </div>
      <span class="ns-verdict__tag">{{ verdict.tag }}</span>
    </section>

    <section class="ns-info">
      <div class="ns-info__row">
        <span class="ns-info__key">Connection type</span>
        <span class="ns-info__val">{{ connectionInfo.effectiveType || 'Unknown' }}</span>
      </div>
      <div class="ns-info__row">
        <span class="ns-info__key">Browser estimate</span>
        <span class="ns-info__val">
          {{ connectionInfo.downlink ? connectionInfo.downlink + ' Mbps' : 'Unavailable' }}
        </span>
      </div>
      <div class="ns-info__row">
        <span class="ns-info__key">Online</span>
        <span class="ns-info__val">
          <span :class="['ns-pill', isOnline ? 'ns-pill--ok' : 'ns-pill--bad']">
            {{ isOnline ? 'Yes' : 'No' }}
          </span>
        </span>
      </div>
      <div class="ns-info__row">
        <span class="ns-info__key">Last test</span>
        <span class="ns-info__val">{{ lastTestedAt || '—' }}</span>
      </div>
    </section>

    <p class="ns-disclaimer">
      Throughput is measured against Cloudflare's public speed-test endpoints. Results depend on
      your device, browser, ISP and the load on intermediate networks at test time.
    </p>
  </div>
</template>

<script>
const DOWNLOAD_BYTES = 25 * 1024 * 1024
const UPLOAD_BYTES = 5 * 1024 * 1024
const PING_COUNT = 6
const PING_URL = 'https://speed.cloudflare.com/__down?bytes=1000'
const DOWNLOAD_URL = `https://speed.cloudflare.com/__down?bytes=${DOWNLOAD_BYTES}`
const UPLOAD_URL = 'https://speed.cloudflare.com/__up'

export default {
  name: 'NetworkSpeed',
  data() {
    return {
      isRunning: false,
      errorMessage: '',
      phase: 'idle',
      results: {
        ping: null,
        jitter: null,
        download: null,
        upload: null,
      },
      liveValue: 0,
      lastTestedAt: '',
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      connectionInfo: this.readConnectionInfo(),
      steps: [
        { key: 'ping', label: 'Latency' },
        { key: 'download', label: 'Download' },
        { key: 'upload', label: 'Upload' },
      ],
      gaugeCircumference: 2 * Math.PI * 92,
    }
  },
  computed: {
    hasResult() {
      return (
        this.results.ping !== null ||
        this.results.download !== null ||
        this.results.upload !== null
      )
    },
    phaseLabel() {
      if (this.phase === 'idle') {
        return this.hasResult ? 'Test complete' : 'Ready'
      }
      if (this.phase === 'ping') return 'Measuring latency…'
      if (this.phase === 'download') return 'Measuring download…'
      if (this.phase === 'upload') return 'Measuring upload…'
      if (this.phase === 'done') return 'Test complete'
      if (this.phase === 'error') return 'Test failed'
      return ''
    },
    liveValueLabel() {
      if (this.phase === 'ping') {
        return this.formatPing(this.liveValue)
      }
      if (this.phase === 'download' || this.phase === 'upload') {
        return this.formatSpeed(this.liveValue)
      }
      if (this.phase === 'done' || this.hasResult) {
        return this.formatSpeed(this.results.download)
      }
      return '0'
    },
    liveUnitLabel() {
      if (this.phase === 'ping') return 'ms'
      return 'Mbps'
    },
    verdict() {
      if (!this.hasResult || this.phase !== 'done') return null
      const ping = this.results.ping
      const jitter = this.results.jitter
      const down = this.results.download
      const up = this.results.upload

      if (!this.isOnline) {
        return {
          tone: 'poor',
          tag: 'Offline',
          title: 'OMS will not load — you are offline',
          message: 'Reconnect to the internet and run the test again.',
        }
      }

      const issues = []
      if (ping !== null && ping > 300) issues.push(`high latency (${Math.round(ping)} ms)`)
      if (jitter !== null && jitter > 60) issues.push(`unstable connection (jitter ${Math.round(jitter)} ms)`)
      if (down !== null && down < 3) issues.push(`very low download (${down.toFixed(1)} Mbps)`)
      if (up !== null && up < 1) issues.push(`very low upload (${up.toFixed(1)} Mbps)`)

      const cautions = []
      if (ping !== null && ping > 150 && ping <= 300) cautions.push(`elevated latency (${Math.round(ping)} ms)`)
      if (jitter !== null && jitter > 30 && jitter <= 60) cautions.push(`some jitter (${Math.round(jitter)} ms)`)
      if (down !== null && down >= 3 && down < 10) cautions.push(`modest download (${down.toFixed(1)} Mbps)`)
      if (up !== null && up >= 1 && up < 2) cautions.push(`modest upload (${up.toFixed(1)} Mbps)`)

      if (issues.length) {
        return {
          tone: 'poor',
          tag: 'Poor',
          title: 'OMS will likely feel slow on this connection',
          message: `Limiting factors: ${issues.join(', ')}.`,
        }
      }
      if (cautions.length) {
        return {
          tone: 'workable',
          tag: 'Workable',
          title: 'OMS should work, but may feel sluggish',
          message: `Watch out for ${cautions.join(', ')}.`,
        }
      }

      const isExcellent =
        (ping === null || ping < 80) &&
        (jitter === null || jitter < 20) &&
        (down === null || down >= 25) &&
        (up === null || up >= 5)

      if (isExcellent) {
        return {
          tone: 'excellent',
          tag: 'Excellent',
          title: 'OMS will run smoothly on this connection',
          message: 'Latency, throughput and stability are all well above what OMS needs.',
        }
      }

      return {
        tone: 'good',
        tag: 'Stable',
        title: 'OMS will run reliably on this connection',
        message: 'Latency and throughput are comfortably above what OMS needs for everyday use.',
      }
    },
    gaugeDashOffset() {
      let fraction = 0
      if (this.phase === 'ping') {
        fraction = Math.min(this.liveValue / 200, 1)
        fraction = 1 - fraction
      } else if (this.phase === 'download' || this.phase === 'upload') {
        fraction = Math.min(this.liveValue / 200, 1)
      } else if (this.phase === 'done' || this.hasResult) {
        fraction = Math.min((this.results.download || 0) / 200, 1)
      }
      return this.gaugeCircumference * (1 - fraction)
    },
  },
  mounted() {
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOnline)
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (conn && typeof conn.addEventListener === 'function') {
      conn.addEventListener('change', this.handleConnectionChange)
    }
  },
  beforeDestroy() {
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOnline)
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (conn && typeof conn.removeEventListener === 'function') {
      conn.removeEventListener('change', this.handleConnectionChange)
    }
  },
  methods: {
    readConnectionInfo() {
      if (typeof navigator === 'undefined') return {}
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      if (!conn) return {}
      return {
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
      }
    },
    handleOnline() {
      this.isOnline = navigator.onLine
    },
    handleConnectionChange() {
      this.connectionInfo = this.readConnectionInfo()
    },
    stepStatus(key) {
      const order = ['ping', 'download', 'upload']
      const phaseIdx = order.indexOf(this.phase)
      const stepIdx = order.indexOf(key)
      if (this.phase === 'done') return 'done'
      if (this.phase === 'error') {
        return this.results[key] !== null ? 'done' : 'idle'
      }
      if (phaseIdx === -1) return 'idle'
      if (stepIdx < phaseIdx) return 'done'
      if (stepIdx === phaseIdx) return 'active'
      return 'idle'
    },
    stepStatusClass(key) {
      return `is-${this.stepStatus(key)}`
    },
    formatPing(value) {
      if (value === null || value === undefined || Number.isNaN(value)) return '—'
      return Math.round(value).toString()
    },
    formatSpeed(value) {
      if (value === null || value === undefined || Number.isNaN(value)) return '—'
      if (value >= 100) return value.toFixed(0)
      if (value >= 10) return value.toFixed(1)
      return value.toFixed(2)
    },
    qualityHint(value, kind) {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return kind === 'down' ? 'Run a test to measure' : 'Run a test to measure'
      }
      const downBands = [
        { min: 100, label: 'Excellent — 4K streaming' },
        { min: 25, label: 'Great — HD video' },
        { min: 10, label: 'Good — SD video' },
        { min: 3, label: 'Workable — light browsing' },
        { min: 0, label: 'Slow — may struggle' },
      ]
      const upBands = [
        { min: 50, label: 'Excellent for uploads' },
        { min: 10, label: 'Great for video calls' },
        { min: 3, label: 'Good for everyday use' },
        { min: 1, label: 'Workable — large uploads slow' },
        { min: 0, label: 'Slow — uploads may stall' },
      ]
      const bands = kind === 'down' ? downBands : upBands
      const match = bands.find((b) => value >= b.min)
      return match ? match.label : ''
    },
    async runTest() {
      if (this.isRunning) return
      this.isRunning = true
      this.errorMessage = ''
      this.results = { ping: null, jitter: null, download: null, upload: null }
      this.liveValue = 0

      try {
        await this.testPing()
        await this.testDownload()
        try {
          await this.testUpload()
        } catch (uploadError) {
          this.errorMessage =
            'Upload measurement was blocked by the network or browser, so it has been skipped.'
        }
        this.phase = 'done'
        this.lastTestedAt = new Date().toLocaleString()
      } catch (error) {
        this.phase = 'error'
        this.errorMessage =
          (error && error.message) ||
          'Could not complete the speed test. Check your connection and try again.'
      } finally {
        this.isRunning = false
      }
    },
    async testPing() {
      this.phase = 'ping'
      this.liveValue = 0
      const samples = []
      for (let i = 0; i < PING_COUNT; i++) {
        const t = await this.singlePing()
        samples.push(t)
        this.liveValue = t
      }
      const sorted = [...samples].sort((a, b) => a - b)
      const median = sorted[Math.floor(sorted.length / 2)]
      const mean = samples.reduce((s, v) => s + v, 0) / samples.length
      const jitter =
        samples.reduce((s, v) => s + Math.abs(v - mean), 0) / samples.length
      this.results.ping = median
      this.results.jitter = jitter
    },
    async singlePing() {
      const url = `${PING_URL}&_=${Date.now()}-${Math.random()}`
      const start = performance.now()
      const response = await fetch(url, { cache: 'no-store' })
      await response.arrayBuffer()
      return performance.now() - start
    },
    async testDownload() {
      this.phase = 'download'
      this.liveValue = 0
      const url = `${DOWNLOAD_URL}&_=${Date.now()}`
      const start = performance.now()
      const response = await fetch(url, { cache: 'no-store' })
      if (!response.body) {
        const buf = await response.arrayBuffer()
        const elapsed = (performance.now() - start) / 1000
        const mbps = (buf.byteLength * 8) / elapsed / 1_000_000
        this.results.download = mbps
        this.liveValue = mbps
        return
      }
      const reader = response.body.getReader()
      let received = 0
      let lastTick = start
      let lastBytes = 0
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        received += value.length
        const now = performance.now()
        if (now - lastTick > 200) {
          const intervalSec = (now - lastTick) / 1000
          const intervalBytes = received - lastBytes
          const instMbps = (intervalBytes * 8) / intervalSec / 1_000_000
          this.liveValue = instMbps
          lastTick = now
          lastBytes = received
        }
      }
      const elapsed = (performance.now() - start) / 1000
      const mbps = (received * 8) / elapsed / 1_000_000
      this.results.download = mbps
      this.liveValue = mbps
    },
    async testUpload() {
      this.phase = 'upload'
      this.liveValue = 0
      const payload = new Uint8Array(UPLOAD_BYTES)
      for (let i = 0; i < payload.length; i += 4096) {
        payload[i] = Math.floor(Math.random() * 256)
      }
      const blob = new Blob([payload], { type: 'text/plain' })
      const url = `${UPLOAD_URL}?_=${Date.now()}`
      const start = performance.now()
      const response = await fetch(url, {
        method: 'POST',
        cache: 'no-store',
        body: blob,
      })
      await response.text().catch(() => '')
      const elapsed = (performance.now() - start) / 1000
      const latencySec = (this.results.ping || 0) / 1000
      const adjusted = Math.max(elapsed - latencySec, elapsed * 0.5)
      const mbps = (payload.byteLength * 8) / adjusted / 1_000_000
      this.results.upload = mbps
      this.liveValue = mbps
    },
  },
}
</script>

<style scoped>
.ns-page {
  max-width: 960px;
  margin: 0 auto;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.ns-toolbar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.4rem 0 1rem;
}

.ns-toolbar__accent {
  display: inline-block;
  width: 4px;
  height: 38px;
  border-radius: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.ns-toolbar__title {
  flex: 1 1 auto;
  min-width: 0;
}

.ns-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.ns-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.ns-toolbar__actions {
  flex: 0 0 auto;
}

.ns-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 36px;
  padding: 0 1.05rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.ns-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.ns-btn:focus { outline: none; }
.ns-btn::-moz-focus-inner { border: 0; }

.ns-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
}

.ns-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.3);
  filter: brightness(1.03);
}

.ns-btn--primary:focus-visible {
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22), 0 0 0 3px rgba(22, 160, 122, 0.32);
}

.ns-btn__icon {
  display: inline-block;
}

.ns-spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #ffffff;
  animation: ns-spin 0.7s linear infinite;
}

@keyframes ns-spin {
  to { transform: rotate(360deg); }
}

.ns-alert {
  margin: 0 0 0.85rem;
}

.ns-gauge-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 1.4rem 1.2rem 1.1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.ns-gauge-wrap {
  position: relative;
  width: 220px;
  height: 220px;
}

.ns-gauge {
  width: 100%;
  height: 100%;
}

.ns-gauge__arc {
  transition: stroke-dashoffset 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.ns-gauge__center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
}

.ns-gauge__value {
  font-size: 2.4rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: -0.02em;
  line-height: 1;
}

.ns-gauge__unit {
  margin-top: 0.2rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #16a07a;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.ns-gauge__phase {
  margin-top: 0.6rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.01em;
}

.ns-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 1.1rem;
  flex-wrap: wrap;
  justify-content: center;
}

.ns-step {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 0.01em;
}

.ns-step__dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #eef1f7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  transition: background 0.2s ease, color 0.2s ease;
}

.ns-step__pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 0 0 rgba(22, 160, 122, 0.6);
  animation: ns-pulse 1.4s ease-out infinite;
}

@keyframes ns-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(22, 160, 122, 0.5); }
  70%  { box-shadow: 0 0 0 8px rgba(22, 160, 122, 0); }
  100% { box-shadow: 0 0 0 0 rgba(22, 160, 122, 0); }
}

.ns-step.is-active {
  color: #0e5b46;
}

.ns-step.is-active .ns-step__dot {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.ns-step.is-done {
  color: #1f2a44;
}

.ns-step.is-done .ns-step__dot {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.ns-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
  margin-top: 0.95rem;
}

.ns-metric {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 0.95rem 1rem 0.9rem;
}

.ns-metric__head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

.ns-metric__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ns-metric__icon--ping {
  background: rgba(22, 160, 122, 0.12);
  color: #0e5b46;
}

.ns-metric__icon--down {
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
}

.ns-metric__icon--up {
  background: rgba(217, 119, 6, 0.12);
  color: #b45309;
}

.ns-metric__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  letter-spacing: 0.02em;
}

.ns-metric__value {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
}

.ns-metric__num {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1;
}

.ns-metric__unit {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
}

.ns-metric__sub {
  margin-top: 0.4rem;
  font-size: 0.72rem;
  color: #6b7280;
}

.ns-verdict {
  margin-top: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  border-radius: 12px;
  border: 1px solid transparent;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.ns-verdict__badge {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
}

.ns-verdict__body {
  flex: 1 1 auto;
  min-width: 0;
}

.ns-verdict__title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.005em;
  line-height: 1.3;
}

.ns-verdict__message {
  margin-top: 0.18rem;
  font-size: 0.78rem;
  color: #4f5560;
  line-height: 1.4;
}

.ns-verdict__tag {
  flex: 0 0 auto;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
  color: #1f2a44;
}

.ns-verdict--excellent {
  background: linear-gradient(135deg, rgba(22, 160, 122, 0.10) 0%, rgba(14, 91, 70, 0.10) 100%);
  border-color: rgba(22, 160, 122, 0.35);
}

.ns-verdict--excellent .ns-verdict__badge {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.ns-verdict--excellent .ns-verdict__tag {
  background: rgba(22, 160, 122, 0.18);
  color: #0e5b46;
}

.ns-verdict--good {
  background: rgba(22, 160, 122, 0.08);
  border-color: rgba(22, 160, 122, 0.28);
}

.ns-verdict--good .ns-verdict__badge {
  background: #16a07a;
}

.ns-verdict--good .ns-verdict__tag {
  background: rgba(22, 160, 122, 0.16);
  color: #0e5b46;
}

.ns-verdict--workable {
  background: rgba(217, 119, 6, 0.08);
  border-color: rgba(217, 119, 6, 0.28);
}

.ns-verdict--workable .ns-verdict__badge {
  background: #d97706;
}

.ns-verdict--workable .ns-verdict__tag {
  background: rgba(217, 119, 6, 0.16);
  color: #b45309;
}

.ns-verdict--poor {
  background: rgba(220, 38, 38, 0.08);
  border-color: rgba(220, 38, 38, 0.28);
}

.ns-verdict--poor .ns-verdict__badge {
  background: #dc2626;
}

.ns-verdict--poor .ns-verdict__tag {
  background: rgba(220, 38, 38, 0.16);
  color: #991b1b;
}

.ns-info {
  margin-top: 0.95rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.ns-info__row {
  display: grid;
  grid-template-columns: 200px 1fr;
  padding: 0.7rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
  align-items: center;
}

.ns-info__row:last-child {
  border-bottom: none;
}

.ns-info__row:nth-child(odd) {
  background: #fafbfd;
}

.ns-info__key {
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.ns-info__val {
  font-size: 0.82rem;
  color: #1f2a44;
}

.ns-pill {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
}

.ns-pill--ok {
  background: rgba(22, 160, 122, 0.14);
  color: #0e5b46;
}

.ns-pill--bad {
  background: rgba(220, 38, 38, 0.14);
  color: #991b1b;
}

.ns-disclaimer {
  margin-top: 0.9rem;
  font-size: 0.72rem;
  color: #94a3b8;
  text-align: center;
  line-height: 1.5;
}

@media (max-width: 720px) {
  .ns-metrics {
    grid-template-columns: 1fr;
  }
  .ns-info__row {
    grid-template-columns: 1fr;
    gap: 0.2rem;
  }
}
</style>
